import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Modal from "@material-ui/core/Modal";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { GrDocumentPdf } from "react-icons/gr";
import { LuX } from "react-icons/lu";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75%",
  height: "92%",
  bgcolor: "#ffffff",
  boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.35)",
  p: 0,
  display: "flex",
  flexDirection: "column",
  borderRadius: "0px",
  overflow: "hidden",
  outline: "none",
};

/**
 * CommonPdfPreviewModal
 * Centralized, reusable PDF Previewer Modal component for all PDF exports across VoyCalc.
 */
const CommonPdfPreviewModal = ({
  open,
  onClose,
  title = "PDF Preview",
  subtitle = "A4 Landscape Report • Smart Sea Freight",
  document,
  onDownload,
  downloadFileName = "Report.pdf",
  downloadButtonText = "Download PDF",
  trigger,
  isDownloading = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);

  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenModal = () => {
    if (isControlled) {
      // Controlled mode handled by parent
    } else {
      setInternalOpen(true);
    }
  };

  const handleCloseModal = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
      onClose?.();
    }
  };

  const handleDownload = async () => {
    try {
      setLoadingDownload(true);
      if (onDownload) {
        await onDownload();
      } else if (document) {
        const pdfBlob = await pdf(document).toBlob();
        const url = window.URL.createObjectURL(
          new Blob([pdfBlob], { type: "application/pdf" })
        );
        const element = window.document.createElement("a");
        element.href = url;
        element.setAttribute("download", downloadFileName);
        element.style.display = "none";
        window.document.body.appendChild(element);
        element.click();
        window.document.body.removeChild(element);
      }
    } catch (err) {
      console.error("PDF Download error:", err);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      {trigger && (
        <div onClick={handleOpenModal} className="inline-block cursor-pointer">
          {trigger}
        </div>
      )}

      <Modal
        open={isOpen}
        onClose={handleCloseModal}
        aria-labelledby="common-pdf-preview-modal"
        aria-describedby="modal-with-pdf-preview"
      >
        <Box style={modalStyle}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-none bg-slate-100 border border-slate-200 flex items-center justify-center text-[#313C4C]">
                <GrDocumentPdf size={16} />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#313C4C] leading-none">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-[11px] font-medium text-slate-500 mt-1 leading-none">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleCloseModal}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-none transition-colors border-none bg-transparent cursor-pointer"
            >
              <LuX className="h-5 w-5" />
            </button>
          </div>

          {/* PDF Stage */}
          <div className="flex-1 bg-slate-200/80 p-3 overflow-hidden">
            <div className="w-full h-full rounded-none overflow-hidden shadow-sm bg-white">
              {document ? (
                <PDFViewer
                  width="100%"
                  height="100%"
                  showToolbar={false}
                  style={{ border: "none" }}
                >
                  {document}
                </PDFViewer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-slate-500 text-sm">Generating PDF Preview...</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-white">
            <span className="text-xs font-medium text-slate-500">
              Ready for high-resolution PDF download
            </span>
            <div className="flex items-center gap-2.5">
              <button
                onClick={handleCloseModal}
                className="px-4 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-none transition-colors cursor-pointer"
              >
                Close
              </button>
              <button
                onClick={handleDownload}
                disabled={loadingDownload || isDownloading}
                className="flex items-center gap-2 px-5 py-1.5 text-xs font-semibold text-white bg-[#313C4C] hover:bg-[#1f2732] rounded-none shadow-sm transition-colors cursor-pointer disabled:opacity-50"
              >
                <GrDocumentPdf size={13} />
                <span>
                  {loadingDownload || isDownloading
                    ? "Downloading..."
                    : downloadButtonText}
                </span>
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default CommonPdfPreviewModal;
